import { inject, Injectable } from '@angular/core';
import { PATH_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL, StatefulValue } from './stateful-value.class';
import { dependencies } from './workflow.dependency';
import {
  WorkflowField,
  PrimitiveType,
  CoreControlType,
  CoreFieldReviewRendererType,
  InputModeType,
  LogicFnRef,
  Backend
} from './workflow.type';
import { EnumerationItem } from './enum-item.class';
import { Enumeration } from './enum.class';
import { CustomTextHelper } from '../helper/custom-text-helper/custom-text-helper.class';
import { maskRegistry } from '../directive/input-mask/input-mask.registry';
import { ValidationBase } from './validation-base.class';
import { StatefulService } from '@app/service/stateful.service';
import { SawDust } from '@cudirect/shared-ui-library';

const ENUM_SUPPORT: CoreControlType[] = [
  'bulleted-list' as CoreControlType,
  'dropdown' as CoreControlType,
  'info-row' as CoreControlType,
  'radio' as CoreControlType,
  'selection-grid' as CoreControlType
];

/**
 * This service will handle the instantiation and handling of Fields (now FieldInstances) so that the service can be injected, mocked, etc.
 */
@Injectable({
  providedIn: 'root'
})
export class FieldService {
  private logger = inject(SawDust).withContext({ component: 'FieldService' });
  // Had this an inject(StatefulService) but it broke testing due to the strange way we're mocking the workflow service.
  constructor(private readonly statefulService: StatefulService) {}

  private instances: { [id: string]: FieldInstance } = {};

  public createField(id: string, flowId: string, iterationId?: number, pageIterationId?: number): FieldInstance {
    const validatedId = this.validate(id, iterationId, pageIterationId);
    const instanceId = `${flowId}/${validatedId}`;

    if (this.instances[instanceId]) {
      return this.instances[instanceId];
    }

    const newInstance = new FieldInstance(this, this.statefulService, id, flowId, iterationId, pageIterationId);
    this.instances[instanceId] = newInstance;
    return newInstance;
  }

  /**
   * Below method should be called to clear Field
   * Make sure this method called only once
   */
  public clear(): void {
    for (const [id] of Object.entries(this.instances)) {
      delete this.instances[id];
    }
  }

  public ensureConfig(configId: string): void {
    const config = dependencies.config.fields[configId];
    if (!config) {
      return;
    }

    // ensure consistency with IDs
    config.id = configId;

    // ensure existing object
    config.validation = config.validation || [];
    config.validationCondition = config.validationCondition || {};

    // implicit storage ID: "mem:field-id"
    config.storageId = config.storageId || `mem:${configId}`;
  }

  /**
   * Test configuration values for validity + set sane defaults (e.g. empty object where missing, adding implicit IDs, etc)
   * Throws errors on integration fails
   * @param id card ID from `workflow-cards.json`
   */
  public test(id: string): void {
    const config = dependencies.config.fields[id];

    // mask check
    if (config.mask) {
      const mask = maskRegistry[config.mask];
      if (!mask) {
        throw new Error(`Workflow field "${config.id}" has invalid mask: ${JSON.stringify(config.mask)}`);
      }
    }

    // logic functions
    dependencies.logicService.testConfig(config, 'field', 'enabled', id);
    dependencies.logicService.testConfig(config, 'field', 'onChange', id);
    dependencies.logicService.testConfig(config, 'field', 'visible', id);

    // implicit storage ID: "mem:field-id"
    config.storageId = config.storageId || 'mem:' + config.id;

    // storage config
    const storageIdParts = config.storageId.split(':');
    const backendType = storageIdParts.shift();
    const backendId = storageIdParts.join(':');
    const storage = dependencies.config.storage[backendType]?.[backendId] || ({} as Storage);

    // checkbox must have a boolean storage type
    if (config.type === 'checkbox' && storage.type !== 'boolean') {
      if (!config.map && storage.type) {
        throw new Error(`Workflow field "${config.id}" is checkbox, but its storage type is ${storage.type || 'string (by implication)'}`);
      }
    }

    // nested fields check
    if (config.nestedFields) {
      this.handleNestedFields(config, id);
    }

    // enum referencing
    if (ENUM_SUPPORT.indexOf(config.type) < 0) {
      if (config.enum) {
        throw new Error(`Workflow field "${config.id}" has enum ${JSON.stringify(config.enum)} despite not being enum type`);
      }
    } else if (!config.enum && config.storageId) {
      // add enum ref from storage config (unless specified on field)
      const [protocol, path] = config.storageId.split(':');
      const storageCfg = dependencies.config.storage[protocol as Backend][path];
      config.enum = storageCfg?.enum;
    }
  }

  // Separated this logic from above for cognitive complexity.
  public handleNestedFields(config, id) {
    if (!Array.isArray(config.nestedFields)) {
      throw new Error(`Workflow field "${config.id}" has invalid nestedFields entry: ${JSON.stringify(config.nestedFields)}`);
    }
    for (const nestedField of config.nestedFields) {
      if (!nestedField || typeof nestedField !== 'string') {
        throw new Error(`nested field reference is not a non-empty string on field ${id}: ${JSON.stringify(nestedField)}`);
      }
      if (!dependencies.config.fields[nestedField]) {
        throw new Error(`nested field id not found on workflow fields definition (on field ${id}: ${nestedField})`);
      }
    }
  }

  public validate(id: string, iterationId: number, pageIterationId: number) {
    // if (id.startsWith('oao-beneficiary-type')) {
    //   this.logger.info('HERE IT IS');
    // }
    let currentPageId = id;
    if (iterationId != null) {
      if (id.indexOf(PATH_ITERATION_SYMBOL) < 0) {
        throw new Error(`Iteration ID provided for non-array field ID: ${id} : ${iterationId}`);
      }
      currentPageId = currentPageId.replace(PATH_ITERATION_SYMBOL, String(iterationId));
    } else if (id.indexOf(PATH_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array field ID requires iteration ID: ${id}`);
    }

    if (pageIterationId != null) {
      if (id.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0) {
        throw new Error(`Page iteration ID provided for non-array field ID: ${id} : ${pageIterationId}`);
      }
      // this.logger.info('About to change page id', { currentPageId });
      currentPageId = currentPageId.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
      // this.logger.info('Changed page id', { currentPageId });
    } else if (id.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array field ID requires page iteration ID: ${id}`);
    }
    return currentPageId;
  }

  /**
   * Determine if field has data
   * @param field testing field
   */
  // TODO: This methods seems inappropriately named?
  public hasNoData(field: FieldInstance): boolean {
    if (field.config) {
      return ['button', 'field-group'].includes(field.config.type);
    }
  }
}

// ************************************************************************************************

/**
 * Field specific logic and configuration store
 * This replaces the old Field class, but functions the same.
 */
export class FieldInstance extends ValidationBase {
  /**
   * Field config as specified at `workflow-fields.json`
   */
  public readonly config: WorkflowField;

  /**
   * Many to one data store unit association
   */
  public readonly data: StatefulValue;

  /**
   * Dirty flag
   */
  public dirty = false;

  /**
   * Focused state
   */
  public focused = false;

  /**
   * Fields in a group - ordered list of references
   */
  public readonly nestedFields: FieldInstance[] = [];

  public readonly instanceId: string;

  /**
   * Create or return a unique Field
   * @param id config id (as set in `workflow-fields.json`)
   * @param iterationId optional iteration ID (for loops)
   * @param pageIterationId optional page loop ID
   */
  constructor(
    public readonly fieldService: FieldService,
    public readonly statefulService: StatefulService,
    public readonly id: string,
    public readonly flowId: string,
    public readonly iterationId?: number,
    public readonly pageIterationId?: number
  ) {
    super();
    this.id = fieldService.validate(id, iterationId, pageIterationId);
    this.instanceId = `${flowId}/${this.id}`;

    this.config = this.createConfigInstance(flowId, id);

    if (!this.config) {
      throw new Error(`No configuration found for fieldId: ${id}`);
    }

    // attach nested field instances
    this.attachNestedFields(flowId, iterationId, pageIterationId);

    if (!this.fieldService.hasNoData(this) || this.config.storageId) {
      this.data = this.statefulService.wrap(this.config.storageId, iterationId, pageIterationId);
      this.setOnChangeEvent();
    }
  }

  setOnChangeEvent(): void {
    if (this.config.onChange) {
      this.data.onChange(() => {
        dependencies.logicService.evaluate(this.config.onChange, { field: this });
        this.revalidate();
      });
    }
  }

  private createConfigInstance(flowId: string, id: string): WorkflowField {
    const instanceConfigId = `${flowId}/${id}`;

    if (dependencies.config.workflowSpecificFields[instanceConfigId]) {
      return { ...dependencies.config.fields[id], ...dependencies.config.workflowSpecificFields[instanceConfigId] };
    }
    return dependencies.config.fields[id];
  }

  private attachNestedFields(flowId: string, iterationId: number, pageIterationId: number) {
    // attach nested field instances
    for (const nestedFieldId of this.config.nestedFields || []) {
      const nestedField = this.fieldService.createField(nestedFieldId, flowId, iterationId, pageIterationId);
      nestedField.config.visible = nestedField.config.visible || this.config.visible;
      this.nestedFields.push(nestedField);
    }
  }

  /**
   * Return parsed field payload which is set in configuration time.
   * Example:
   *  "field": {
   *             label: "Label",
   *             payload: {
   *                        "xyz": "#{protocol:xyz/abc}"
   *                      }
   *           }
   */
  public get payload(): { [key: string]: PrimitiveType } {
    if (!this.config.payload) {
      return {};
    }

    const result = {};
    for (const [key, value] of Object.entries(this.config.payload)) {
      result[key] = dependencies.stringVariableService.parse(value, this.iterationId, this.pageIterationId);
    }

    return result;
  }

  /**
   * Whether field should be editable (e.g. not loading/saving or failing the `enabled` logic fn)
   */
  public get enabled(): boolean {
    return dependencies.logicService.evaluate(this.config.enabled, { field: this }, true);
  }

  /**
   * Dynamic enum (supports stateful value references)
   */
  public get enum(): EnumerationItem[] {
    const ref = dependencies.stringVariableService.parse(this.config.enum) as string;
    return ref ? new Enumeration(ref, true, this).values : [];
  }

  /**
   * @inheritdoc
   */
  public hasValidation(fnName: string): PrimitiveType[] {
    return dependencies.logicService.hasFunction(fnName, this.data.validationLogicFn, this.validations);
  }

  /**
   * Check if field has required validation
   */
  public get required(): boolean {
    // TODO: config.required -> config.validation.required for every JSON file
    // return !!this.hasValidation('required') || this.config.required;
    return !!this.hasValidation('required');
  }

  public get configLabel(): string {
    return dependencies.stringVariableService.parse(this.config.label, this.pageIterationId) as string;
  }

  public get isReviewableField(): boolean {
    return !this.hideInReview && this.config.type !== 'button' && (this.config.alwaysShowInReview || (this.visible && this.enabled));
  }

  // mobile keyboard input mode, default is text
  public get inputMode(): InputModeType {
    return this.config.inputMode ? this.config.inputMode : 'text';
  }

  /**
   * Processed label
   */
  public get label(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.labelFn,
      { field: this },
      this.configLabel,
      this.iterationId,
      this.pageIterationId
    );
  }

  /**
   * processed content for info row type
   */
  public get content(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.contentFn,
      { field: this },
      this.config.content,
      this.iterationId,
      this.pageIterationId
    );
  }

  /**
   * get parsed and computed tooltip text
   */
  public get tooltip(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.tooltipFn,
      { field: this },
      this.config.tooltip,
      this.iterationId,
      this.pageIterationId
    );
  }

  public get description(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.descriptionFn,
      { field: this },
      this.config.description,
      this.iterationId,
      this.pageIterationId
    );
  }

  public get infoTip(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.infoTipFn,
      { field: this },
      this.config.infoTip,
      this.iterationId,
      this.pageIterationId
    );
  }

  public get infoTipPrompt(): string {
    return CustomTextHelper.customTextOrDefault(
      this.config.infoTipPromptFn,
      { field: this },
      this.config.infoTipPrompt,
      this.iterationId,
      this.pageIterationId
    );
  }

  public parse(property: string): string {
    return dependencies.stringVariableService.parse(property, this.iterationId, this.pageIterationId) as string;
  }

  /**
   * Transparently return human-readable enum part if provided (otherwise just return value)
   */
  public get readableValue(): string {
    const enumItem = this.config.enum && this.enum.find((item) => item?.value?.toString() === this.data.valueStringOnly);
    return enumItem
      ? enumItem.label
      : String((this.data.value !== null && this.data.value !== undefined && this.data.value.toString()) || '');
  }

  /**
   * Whether field should be visible
   */
  public get visible(): boolean {
    if (typeof this.config.visible === 'boolean') {
      return this.config.visible;
    }

    const isVisible = dependencies.logicService.evaluate(
      this.config.visible,
      { field: this },
      true,
      this.iterationId,
      this.pageIterationId
    );
    if (isVisible === false) {
      this.dirty = false;
    }

    if (this.isNested && isVisible) {
      return this.nestedFields.filter((t) => t.visible).length > 0;
    }

    return isVisible;
  }

  /**
   * Whether it is a nested field type
   */
  public get isNested(): boolean {
    return this.config.type === 'field-group';
  }

  public get reviewRendererType(): CoreFieldReviewRendererType {
    const defaultRendererType: CoreFieldReviewRendererType = 'standard-field-review-renderer';
    return this.config.reviewRendererType || defaultRendererType;
  }

  /**
   * Trigger revalidation
   */
  public revalidate(): string[] {
    if (this.enabled && this.visible) {
      return this.spreadValidation(
        this.data.revalidate(),
        dependencies.logicService.evaluate(this.validations, { field: this }, 'collect', this.iterationId, this.pageIterationId)
      );
    }
    return [];
  }

  /**
   * Returns filtered (by validationCondition) list of validations
   */
  public get validations(): LogicFnRef[] {
    return this.config.validation
      .filter((logicFnDesc) => {
        const name: string = Array.isArray(logicFnDesc) ? logicFnDesc[0] : logicFnDesc;
        if (this.config.validationCondition[name]) {
          const fnCondition = this.config.validationCondition[name];
          return dependencies.logicService.evaluate(fnCondition, { field: this }, true, this.iterationId, this.pageIterationId);
        }
        return true;
      })
      .map((logicFnDesc) => {
        const name: string = Array.isArray(logicFnDesc) ? logicFnDesc[0] : logicFnDesc;
        if (this.config?.validationOverride?.[name]) {
          return this.config?.validationOverride[name];
        }
        return logicFnDesc;
      });
  }

  public get hideInReview(): boolean {
    if (!this.config.hideInReview) {
      return false;
    }

    if (typeof this.config.hideInReview === 'boolean') {
      return this.config.hideInReview;
    }

    return dependencies.logicService.evaluate(this.config.hideInReview, { field: this }, true, null, this.pageIterationId);
  }

  /**
   * Adds an optional automation ID as defined in the workflow.jsons
   * Will fall back to the field ID if no automation ID is defined
   * @returns automation Id
   */
  public get automationId(): string {
    return this.config?.automationId ? this.config.automationId : this.id;
  }
  /**
   * Returns Max length limitation for field
   */
  public get fieldMaxLength(): number {
    if (typeof this.config.fieldMaxLength === 'undefined' || typeof this.config.fieldMaxLength === 'number') {
      return this.config.fieldMaxLength;
    }
    if (typeof this.config.fieldMaxLength === 'string')
      return dependencies.stringVariableService.parse(this.config.fieldMaxLength, this.iterationId, this.pageIterationId) as number;

    const res = dependencies.logicService.evaluate<number>(
      this.config.fieldMaxLength,
      { field: this },
      'single',
      null,
      this.pageIterationId
    );
    return Array.isArray(res) ? res[0] : res;
  }
}

// Example Usage in Component
// constructor(private fieldService: FieldService) {}
// const fieldInstance = this.fieldService.createField('id123', 'flow1');
